import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { toast, ToastContainer } from "react-toastify";
import ToastAlert, { ToastCloseIcon } from "../ToastAlert/ToastAlert.jsx";
import PageBanner from "../PageBanner/PageBanner.jsx";
import ContainerBleed from "../Container/ContainerBleed.jsx";
import ContainerFull from "../Container/ContainerFull.jsx";
import VUApiHistoryDetailsModal from "./VUApiHistoryDetailsModal.jsx";
import { CopyButton } from "../APIKeyManager/APIKeyManager.jsx";

/* ==================================================================
 * VUApiHistory
 * -----------------------------------------------------------------
 * Admin SPA for auditing calls made through the Virtual Underwriter
 * API (`/api/ApiClient/audit`). Renders a
 * filterable, paged table of VuAuditHistoryItem records with method /
 * status / request-type badges. Each row exposes a Details button
 * that opens VUApiHistoryDetailsModal to inspect the recorded
 * response data, request body, and error message -- fields that
 * would clutter the row summary but are essential when triaging a
 * failed call.
 * ================================================================== */
const AUDIT_ENDPOINT = "/api/ApiClient/audit";
const CLIENTS_ENDPOINT = "/api/ApiClient";
const REQUEST_TIMEOUT_MS = 20000;
const MAX_ERROR_LENGTH = 300;
const DEFAULT_PAGE_SIZE = 10;
const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];
const REQUEST_TYPE_OPTIONS = [
  { label: "All Types", value: "" },
  { label: "User", value: "User" },
  { label: "Content", value: "Content" },
  { label: "Unknown", value: "Unknown" },
];

const sanitizeMessage = value => {
  if (value === null || value === undefined) return "";
  let s = String(value);
  s = s.replace(/[\u0000-\u0008\u000B-\u001F\u007F]/g, " ");
  s = s.replace(/\s+/g, " ").trim();
  if (s.length > MAX_ERROR_LENGTH) {
    s = `${s.slice(0, MAX_ERROR_LENGTH - 1)}\u2026`;
  }
  return s;
};

const readServerError = async response => {
  let rawBody = "";
  try {
    rawBody = await response.text();
  } catch (e) {
    return "";
  }
  let data = null;
  try {
    data = rawBody ? JSON.parse(rawBody) : null;
  } catch (e) {
    /* fall through */
  }
  if (data) {
    const message =
      data.message ||
      data.Message ||
      data.exceptionMessage ||
      data.ExceptionMessage;
    if (message) return sanitizeMessage(message);
  }
  if (rawBody && !/<html[\s>]/i.test(rawBody)) {
    return sanitizeMessage(rawBody);
  }
  return "";
};

const makeApiError = (category, message, status) => {
  const err = new Error(message);
  err.category = category;
  if (typeof status === "number") err.status = status;
  return err;
};

const messageForStatus = (status, detail) => {
  if (status === 401 || status === 403) {
    return "You do not have permission to view the audit log, or your session has expired. Reload the page and sign in again.";
  }
  if (status === 404) {
    return detail || "The audit log could not be found.";
  }
  if (status >= 400 && status < 500) {
    return detail || `The server rejected the request (HTTP ${status}).`;
  }
  if (status >= 500) {
    return "The server ran into an error. Please try again in a moment.";
  }
  return detail || `Unexpected response (HTTP ${status}).`;
};

const showToast = (variant, title, message) => {
  toast(
    <ToastAlert style={variant} title={title} description={message} />,
    {

      className: "st-toast-alert vu-api-history__toast",
      closeButton: <ToastCloseIcon />,
    },
  );
};

const formatDateTime = value => {
  if (!value) return "\u2014";
  const d = new Date(value);
  if (isNaN(d.getTime())) return "\u2014";
  return d.toLocaleString();
};

const methodVariant = method => {
  const m = (method || "").toUpperCase();
  if (["GET", "POST", "PUT", "PATCH", "DELETE"].indexOf(m) !== -1) {
    return m.toLowerCase();
  }
  return "unknown";
};

const statusVariant = code => {
  if (!code) return "unknown";
  if (code >= 200 && code < 300) return "2xx";
  if (code >= 300 && code < 400) return "3xx";
  if (code >= 400 && code < 500) return "4xx";
  if (code >= 500) return "5xx";
  return "unknown";
};

/* ==================================================================
 * Pagination
 * -----------------------------------------------------------------
 * Numbered pager with Previous / Next controls plus a page-size
 * selector. Reuses the same visual style as APIKeyManager's pager
 * so admin surfaces feel unified.
 * ================================================================== */
const Pagination = ({
  currentPage,
  totalPages,
  pageSize,
  totalRecords,
  onPageChange,
  onPageSizeChange,
}) => {
  const startEntry = totalRecords === 0 ? 0 : (currentPage - 1) * pageSize + 1;
  const endEntry = Math.min(currentPage * pageSize, totalRecords);

  const pages = [];
  const maxVisible = 5;
  let start = Math.max(1, currentPage - Math.floor(maxVisible / 2));
  const end = Math.min(totalPages, start + maxVisible - 1);
  if (end - start < maxVisible - 1) {
    start = Math.max(1, end - maxVisible + 1);
  }
  for (let i = start; i <= end; i++) pages.push(i);

  const go = p => {
    if (p >= 1 && p <= totalPages && p !== currentPage) onPageChange(p);
  };

  return (
    <div className="vu-api-history__pagination-wrap">
      <div className="vu-api-history__pagination-info">
        <label className="vu-api-history__page-size">
          <span className="vu-api-history__page-size-label">
            Rows per page
          </span>
          <select
            value={pageSize}
            onChange={e => onPageSizeChange(parseInt(e.target.value, 10))}
            aria-label="Rows per page"
          >
            {PAGE_SIZE_OPTIONS.map(o => (
              <option key={o} value={o}>
                {o}
              </option>
            ))}
          </select>
        </label>
        <span className="vu-api-history__pagination-summary">
          Showing {startEntry}&ndash;{endEntry} of {totalRecords} records
        </span>
      </div>
      {totalPages > 1 ? (
        <nav
          className="vu-api-history__pagination"
          role="navigation"
          aria-label="Audit history pages"
        >
          <button
            type="button"
            className="btn vu-api-history__page-btn"
            onClick={() => go(currentPage - 1)}
            disabled={currentPage === 1}
            aria-label="Previous page"
          >
            Previous
          </button>
          <ul className="vu-api-history__pages">
            {start > 1 && (
              <li
                className="vu-api-history__pages-ellipsis"
                aria-hidden="true"
              >
                &hellip;
              </li>
            )}
            {pages.map(p => (
              <li key={p}>
                <button
                  type="button"
                  className={`btn vu-api-history__page-btn${p === currentPage ? " is-current" : ""}`}
                  onClick={() => go(p)}
                  aria-current={p === currentPage ? "page" : undefined}
                  aria-label={`Go to page ${p}`}
                >
                  {p}
                </button>
              </li>
            ))}
            {end < totalPages && (
              <li
                className="vu-api-history__pages-ellipsis"
                aria-hidden="true"
              >
                &hellip;
              </li>
            )}
          </ul>
          <button
            type="button"
            className="btn vu-api-history__page-btn"
            onClick={() => go(currentPage + 1)}
            disabled={currentPage === totalPages}
            aria-label="Next page"
          >
            Next
          </button>
        </nav>
      ) : null}
    </div>
  );
};

const VUApiHistory = () => {
  const [items, setItems] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [loading, setLoading] = useState(false);
  const [clients, setClients] = useState([]);
  const [selected, setSelected] = useState(null);

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  const [filters, setFilters] = useState({
    apiClientId: "",
    requestType: "",
    fromDate: "",
    toDate: "",
  });
  const [pendingFilters, setPendingFilters] = useState({
    apiClientId: "",
    requestType: "",
    fromDate: "",
    toDate: "",
  });

  const totalPages = Math.max(1, Math.ceil(totalRecords / pageSize));

  const abortersRef = useRef(new Set());
  const isMountedRef = useRef(true);
  /* Monotonic request counter for fetchHistory. Guards against stale
   * responses overwriting fresh ones when the user rapidly changes
   * filters / page / page-size: each call captures its own epoch and
   * only commits the result if it is still the latest. Without this,
   * a slow response from an older filter set can arrive after a new
   * one and clobber the UI. */
  const historyEpochRef = useRef(0);
  useEffect(
    () => () => {
      isMountedRef.current = false;
      abortersRef.current.forEach(c => {
        try {
          c.abort();
        } catch (e) {
          /* noop */
        }
      });
      abortersRef.current.clear();
    },
    [],
  );

  const apiRequest = useCallback(async (url, options = {}) => {
    const controller = new AbortController();
    abortersRef.current.add(controller);
    const timeoutId = setTimeout(() => {
      controller.__timedOut = true;
      controller.abort();
    }, REQUEST_TIMEOUT_MS);
    const headers = {
      Accept: "application/json",
      ...(options.headers || {}),
    };
    let response;
    try {
      response = await fetch(url, {
        credentials: "same-origin",
        ...options,
        headers,
        signal: controller.signal,
      });
    } catch (err) {
      if (err && err.name === "AbortError") {
        if (controller.__timedOut) {
          throw makeApiError(
            "timeout",
            "The request timed out. Please check your connection and try again.",
          );
        }
        throw makeApiError("aborted", "Request cancelled.");
      }
      throw makeApiError(
        "network",
        "Could not reach the server. Please check your connection and try again.",
      );
    } finally {
      clearTimeout(timeoutId);
      abortersRef.current.delete(controller);
    }
    if (!response.ok) {
      const detail = await readServerError(response);
      const status = response.status;
      let category = "server";
      if (status === 401 || status === 403) category = "auth";
      else if (status >= 400 && status < 500) category = "validation";
      throw makeApiError(category, messageForStatus(status, detail), status);
    }
    if (response.status === 204) return null;
    let text = "";
    try {
      text = await response.text();
    } catch (e) {
      throw makeApiError(
        "parse",
        "The server returned an unreadable response.",
      );
    }
    if (!text) return null;
    try {
      return JSON.parse(text);
    } catch (e) {
      throw makeApiError(
        "parse",
        "The server returned an unexpected response. You may have been signed out \u2014 try reloading the page.",
      );
    }
  }, []);

  const handleError = (title, err) => {
    if (!err || err.category === "aborted") return;
    if (!isMountedRef.current) return;
    showToast("error", title, err.message || "Something went wrong.");
  };

  /* Client dropdown feed — one-shot fetch on mount. Reads from the
   * same ApiClient list endpoint used by APIKeyManager so the filter
   * shows exactly the clients an admin can manage. */
  const fetchClients = useCallback(async () => {
    try {
      const data = await apiRequest(CLIENTS_ENDPOINT, { method: "GET" });
      if (!isMountedRef.current) return;
      const list = (Array.isArray(data) ? data : []).map(c => ({
        id: c.id ?? c.Id,
        clientName: c.clientName ?? c.ClientName,
      }));
      setClients(list);
    } catch (err) {
      /* Non-fatal — filter just falls back to free entry. */
      if (err && err.category !== "aborted") {
        // eslint-disable-next-line no-console
        console.warn("VUApiHistory: could not load client list", err);
      }
    }
  }, [apiRequest]);

  const fetchHistory = useCallback(async () => {
    const epoch = ++historyEpochRef.current;
    if (isMountedRef.current) setLoading(true);
    try {
      /* [FromUri] binding in Web API 2 is case-insensitive, and
       * Bhavin's controllers don't pin the shared JsonFormatter
       * resolver, so responses may arrive in camelCase OR PascalCase
       * depending on which controller last ran on the worker. Sending
       * PascalCase query params matches Bhavin's DTO names verbatim;
       * reading both casings on the response side handles the race. */
      const params = new URLSearchParams();
      params.set("PageNumber", String(page));
      params.set("PageSize", String(pageSize));
      if (filters.apiClientId) params.set("ApiClientId", filters.apiClientId);
      if (filters.requestType) params.set("RequestType", filters.requestType);
      if (filters.fromDate) params.set("FromDate", filters.fromDate);
      if (filters.toDate) params.set("ToDate", filters.toDate);

      const data = await apiRequest(
        `${AUDIT_ENDPOINT}?${params.toString()}`,
        { method: "GET" },
      );
      if (!isMountedRef.current) return;
      /* Stale-guard: a newer fetch has already superseded this one
       * (user changed filters / page mid-flight). Drop silently. */
      if (epoch !== historyEpochRef.current) return;

      const rawItems =
        (data && (data.items ?? data.Items)) || [];
      const list = rawItems.map(i => ({
        id: i.id ?? i.Id,
        clientName: i.clientName ?? i.ClientName,
        requestType: i.requestType ?? i.RequestType,
        requestTime: i.requestTime ?? i.RequestTime,
        requestUrl: i.requestUrl ?? i.RequestUrl,
        httpMethod: i.httpMethod ?? i.HttpMethod,
        requestBody: i.requestBody ?? i.RequestBody,
        statusCode: i.statusCode ?? i.StatusCode,
        isSuccess: i.isSuccess ?? i.IsSuccess,
        responseData: i.responseData ?? i.ResponseData,
        errorMessage: i.errorMessage ?? i.ErrorMessage,
      }));
      setItems(list);
      setTotalRecords(
        (data && (data.totalRecords ?? data.TotalRecords)) || 0,
      );
    } catch (err) {
      if (epoch !== historyEpochRef.current) return;
      handleError("Could not load audit history", err);
      if (isMountedRef.current) {
        setItems([]);
        setTotalRecords(0);
      }
    } finally {
      /* Only the latest in-flight request owns the loading spinner —
       * an older superseded call must not flip it off while a newer
       * one is still running. */
      if (epoch === historyEpochRef.current && isMountedRef.current) {
        setLoading(false);
      }
    }
  }, [apiRequest, page, pageSize, filters]);

  useEffect(() => {
    fetchClients();
  }, [fetchClients]);

  useEffect(() => {
    fetchHistory();
  }, [fetchHistory]);

  const applyFilters = e => {
    if (e) e.preventDefault();
    setPage(1);
    setFilters(pendingFilters);
  };

  const resetFilters = () => {
    const empty = {
      apiClientId: "",
      requestType: "",
      fromDate: "",
      toDate: "",
    };
    setPendingFilters(empty);
    setFilters(empty);
    setPage(1);
  };

  const responseSummary = useMemo(
    () => item => {
      const data = item.responseData;
      if (!data) return "\u2014";
      const total = data.totalItems ?? data.TotalItems;
      if (typeof total === "number") {
        return `${total} item${total === 1 ? "" : "s"}`;
      }
      const rawItems = data.items ?? data.Items;
      if (Array.isArray(rawItems)) {
        return `${rawItems.length} item${rawItems.length === 1 ? "" : "s"}`;
      }
      return "\u2014";
    },
    [],
  );

  return (
    <>
      <ContainerBleed backgroundColor="#f2f2f2">
        <PageBanner
          darkText={true}
          showAccentLine={true}
          title="API Request History"
        />
      </ContainerBleed>
      <ContainerFull style={{ paddingBlock: "4rem" }}>
        <div className="vu-api-history">
          <header className="vu-api-history__header">
            <div className="vu-api-history__header-text">
              <div
                className="vu-api-history__title"
                role="heading"
                aria-level={2}
              >
                Recent API Requests
              </div>
              <p className="vu-api-history__subtitle">
                Audit incoming Virtual Underwriter API traffic. Filter by
                client, request type, or date range and open any row to
                inspect the request and response payload.
              </p>
            </div>
            <div className="vu-api-history__header-actions">
              <a
                className="vu-api-history__secondary-action"
                href="/sitecore/shell/Applications/VUApiKeyManager.aspx"
              >
                <span
                  className="vu-api-history__secondary-action-icon"
                  aria-hidden="true"
                >
                  <svg viewBox="0 0 16 16" width="14" height="14">
                    <path
                      d="M9.5 3 4.5 8l5 5"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.75"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </span>
                Back to API Clients
              </a>
              <button
                type="button"
                className="btn btn--primary vu-api-history__primary-action"
                onClick={() => fetchHistory()}
                disabled={loading}
                aria-label="Refresh audit history"
              >
              <span
                className="vu-api-history__primary-action-icon"
                aria-hidden="true"
              >
                <svg viewBox="0 0 16 16" width="14" height="14">
                  <path
                    d="M13.5 8a5.5 5.5 0 1 1-1.61-3.89"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.75"
                    strokeLinecap="round"
                  />
                  <path
                    d="M13.5 2.5v3h-3"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.75"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </span>
              Refresh
            </button>
            </div>
          </header>

          <form
            className="vu-api-history__filters"
            onSubmit={applyFilters}
            role="search"
            aria-label="Filter audit history"
          >
            <label className="vu-api-history__filter">
              <span className="vu-api-history__filter-label">Client</span>
              <select
                value={pendingFilters.apiClientId}
                onChange={e =>
                  setPendingFilters(f => ({
                    ...f,
                    apiClientId: e.target.value,
                  }))
                }
              >
                <option value="">All clients</option>
                {clients.map(c => (
                  <option key={c.id} value={c.id}>
                    {c.clientName}
                  </option>
                ))}
              </select>
            </label>

            <label className="vu-api-history__filter">
              <span className="vu-api-history__filter-label">Type</span>
              <select
                value={pendingFilters.requestType}
                onChange={e =>
                  setPendingFilters(f => ({
                    ...f,
                    requestType: e.target.value,
                  }))
                }
              >
                {REQUEST_TYPE_OPTIONS.map(o => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </label>

            <label className="vu-api-history__filter">
              <span className="vu-api-history__filter-label">From</span>
              <input
                type="date"
                value={pendingFilters.fromDate}
                onChange={e =>
                  setPendingFilters(f => ({
                    ...f,
                    fromDate: e.target.value,
                  }))
                }
              />
            </label>

            <label className="vu-api-history__filter">
              <span className="vu-api-history__filter-label">To</span>
              <input
                type="date"
                value={pendingFilters.toDate}
                onChange={e =>
                  setPendingFilters(f => ({
                    ...f,
                    toDate: e.target.value,
                  }))
                }
              />
            </label>

            <div className="vu-api-history__filter-actions">
              <button
                type="submit"
                className="btn btn--primary vu-api-history__filter-apply"
                disabled={loading}
              >
                Apply
              </button>
              <button
                type="button"
                className="btn btn--secondary vu-api-history__filter-reset"
                onClick={resetFilters}
                disabled={loading}
              >
                Reset
              </button>
            </div>
          </form>

          <div className="vu-api-history__table-wrap">
            {loading && items.length === 0 ? (
              <p className="vu-api-history__empty">Loading history&hellip;</p>
            ) : items.length === 0 ? (
              <p className="vu-api-history__empty">
                No audit records match the current filters.
              </p>
            ) : (
              <table
                className="vu-api-history__table"
                aria-label="API request audit log"
              >
                <thead>
                  <tr>
                    <th scope="col">Time</th>
                    <th scope="col">Client</th>
                    <th scope="col">Type</th>
                    <th scope="col">Method</th>
                    <th scope="col">URL</th>
                    <th scope="col">Status</th>
                    <th scope="col">Response</th>
                    <th
                      scope="col"
                      className="vu-api-history__col-actions"
                    >
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {items.map(item => {
                    const method = (item.httpMethod || "").toUpperCase();
                    return (
                      <tr key={item.id}>
                        <td
                          className="vu-api-history__cell-time"
                          data-label="Time"
                        >
                          {formatDateTime(item.requestTime)}
                        </td>
                        <td data-label="Client">
                          {item.clientName || "\u2014"}
                        </td>
                        <td data-label="Type">
                          <span
                            className={`vu-api-history__badge vu-api-history__badge--type-${(item.requestType || "unknown").toLowerCase()}`}
                          >
                            {item.requestType || "Unknown"}
                          </span>
                        </td>
                        <td data-label="Method">
                          <span
                            className={`vu-api-history__badge vu-api-history__badge--method-${methodVariant(method)}`}
                          >
                            {method || "\u2014"}
                          </span>
                        </td>
                        <td
                          className="vu-api-history__cell-url"
                          data-label="URL"
                          data-url={item.requestUrl || ""}
                          title={item.requestUrl || ""}
                        >
                          {item.requestUrl ? (
                            <div className="vu-api-history__url-wrap">
                              <span className="vu-api-history__url-text">
                                {item.requestUrl}
                              </span>
                              <CopyButton
                                value={item.requestUrl}
                                label="Copy URL"
                              />
                            </div>
                          ) : (
                            <span className="vu-api-history__url-text">
                              {"\u2014"}
                            </span>
                          )}
                        </td>
                        <td data-label="Status">
                          <span
                            className={`vu-api-history__badge vu-api-history__badge--status-${statusVariant(item.statusCode)}`}
                          >
                            {item.statusCode ?? "\u2014"}
                          </span>
                        </td>
                        <td data-label="Response">
                          {responseSummary(item)}
                        </td>
                        <td
                          className="vu-api-history__col-actions"
                          data-label="Actions"
                        >
                          <button
                            type="button"
                            className="btn vu-api-history__details-btn"
                            onClick={() => setSelected(item)}
                            aria-label={`View request details for ${
                              item.clientName || "unknown client"
                            } at ${formatDateTime(item.requestTime)}`}
                          >
                            Details
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>

          <Pagination
            currentPage={Math.min(page, totalPages)}
            totalPages={totalPages}
            pageSize={pageSize}
            totalRecords={totalRecords}
            onPageChange={setPage}
            onPageSizeChange={size => {
              setPageSize(size);
              setPage(1);
            }}
          />
        </div>
      </ContainerFull>

      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        pauseOnFocusLoss
        pauseOnHover
      />

      {selected ? (
        <VUApiHistoryDetailsModal
          item={selected}
          onClose={() => setSelected(null)}
        />
      ) : null}
    </>
  );
};

export default VUApiHistory;
